import type {
  Consensus,
  LogRow,
  Paper,
  PicotSpice,
  RunState,
  SavedRun,
  SseEvent,
  SpecialistState,
} from "../types/api";
import { initialRunState } from "../types/api";
import { normalizePicot, normalizeSpice } from "../utils/frameworkSlots";
import { activatePhase, doneAllPhases, phaseFromEvent } from "./phaseLogic";

let logCounter = 0;

function specialistKeyFromTool(name: string | undefined): string | null {
  if (!name) return null;
  if (name.endsWith("_specialist")) return name.slice(0, -"_specialist".length);
  if (name.startsWith("search_") && name.endsWith("_papers")) {
    return name.slice("search_".length, -"_papers".length);
  }
  return null;
}

function ensureSpecialistKeys(state: RunState, keys: string[]): Record<string, SpecialistState> {
  if (!keys.length) return state.specialistText;
  let next = state.specialistText;
  for (const key of keys) {
    if (next[key]) continue;
    if (next === state.specialistText) next = { ...next };
    next[key] = { key, paperCount: null, lastTool: null, text: "" };
  }
  return next;
}

function nowIso(): string {
  return new Date().toISOString().slice(11, 19);
}

function logSummary(event: SseEvent): string {
  switch (event.type) {
    case "run_start":
      return String((event.data as { run_id?: string })?.run_id ?? "");
    case "query_rewritten":
      return (event.data as { rewritten_query?: string })?.rewritten_query?.slice(0, 80) ?? "";
    case "query_classified":
      return JSON.stringify(event.data).slice(0, 120);
    case "tool_start":
    case "tool_running":
    case "tool_end":
      return event.name ?? "";
    case "tool_result":
      return `${event.name ?? ""} · ${event.papers?.length ?? 0} papers`;
    case "specialist_papers":
      return `${event.specialist} · ${event.count} papers`;
    case "specialist_text":
      return `${event.specialist} (+${String(event.data ?? "").length} chars)`;
    case "text":
      return `(+${String(event.data ?? "").length} chars)`;
    case "papers":
      return `${(event.data as { count?: number })?.count ?? 0} total`;
    case "consensus":
      return "parsed";
    case "run_end":
      return `${(event.data as { duration_ms?: number })?.duration_ms ?? 0}ms`;
    case "error":
      return String(event.data ?? "");
    default:
      return JSON.stringify(event.data ?? event).slice(0, 100);
  }
}

function logTone(type: string): LogRow["tone"] {
  if (type === "run_start" || type === "done" || type === "run_end") return "success";
  if (type === "error") return "error";
  if (type === "thinking") return "warning";
  return "default";
}

function upsertLog(state: RunState, event: SseEvent): LogRow[] {
  const toolTypes = new Set(["tool_start", "tool_running", "tool_result", "tool_end"]);
  if (toolTypes.has(event.type) && event.id) {
    const existing = state.log.find((r) => r.toolId === event.id);
    if (existing) {
      return state.log.map((r) =>
        r.toolId === event.id
          ? {
              ...r,
              type: event.type,
              summary: logSummary(event),
              done: event.type === "tool_end",
              tone: event.type === "tool_end" ? "success" : r.tone,
            }
          : r
      );
    }
    if (event.type === "tool_end") return state.log;
  }

  const row: LogRow = {
    id: `log-${++logCounter}`,
    timestamp: nowIso(),
    type: event.type,
    summary: logSummary(event),
    tone: logTone(event.type),
    toolId: event.id,
    done: event.type === "tool_end",
  };
  return [...state.log, row];
}

function ensureSpecialist(state: RunState, key: string): SpecialistState {
  return (
    state.specialistText[key] ?? {
      key,
      paperCount: null,
      lastTool: null,
      text: "",
    }
  );
}

function clonePhaseCtx(state: RunState, now: number) {
  return {
    phaseStatus: [...state.phaseStatus],
    phaseStartedAt: [...state.phaseStartedAt],
    phaseElapsedMs: [...state.phaseElapsedMs],
    phase3WasActive: state.phase3WasActive,
    now,
  };
}

export type RunAction =
  | { type: "SET_PROMPT"; prompt: string }
  | { type: "SET_VECTOR_STORE"; store: RunState["vectorStore"] }
  | { type: "SET_DEPARTMENT"; departmentId: string; departmentLabel: string }
  | { type: "SET_DEPARTMENTS"; departments: RunState["departments"] }
  | { type: "SET_DEFAULT_VECTOR_STORE"; store: RunState["vectorStore"] }
  | { type: "START_RUN"; prompt: string; vectorStore: RunState["vectorStore"]; departmentId: string; departmentLabel: string }
  | { type: "SSE_EVENT"; event: SseEvent }
  | { type: "RUN_ERROR"; message: string }
  | { type: "CANCEL_RUN" }
  | { type: "RESET_TO_INPUT" }
  | { type: "GO_RESULTS" }
  | { type: "SHOW_LIVE_LOG"; show: boolean }
  | { type: "LOAD_SAVED_RUN"; run: SavedRun; preserveSession?: boolean }
  | { type: "LOAD_SAVED_RUN_ERROR"; message: string };

export function runReducer(state: RunState, action: RunAction): RunState {
  switch (action.type) {
    case "SET_PROMPT":
      return { ...state, prompt: action.prompt };
    case "SET_VECTOR_STORE":
      return { ...state, vectorStore: action.store };
    case "SET_DEPARTMENT":
      return {
        ...state,
        departmentId: action.departmentId,
        departmentLabel: action.departmentLabel,
      };
    case "SET_DEPARTMENTS":
      return { ...state, departments: action.departments };
    case "SET_DEFAULT_VECTOR_STORE":
      return { ...state, defaultVectorStore: action.store, vectorStore: action.store };
    case "START_RUN":
      logCounter = 0;
      return {
        ...initialRunState(),
        screen: "live",
        prompt: action.prompt,
        vectorStore: action.vectorStore,
        defaultVectorStore: state.defaultVectorStore,
        departmentId: action.departmentId,
        departmentLabel: action.departmentLabel,
        departments: state.departments,
        runStatus: "connecting",
        phaseStatus: ["active", "pending", "pending", "pending", "pending"],
        phaseStartedAt: [Date.now(), null, null, null, null],
        lastEventMs: Date.now(),
      };
    case "CANCEL_RUN":
      return {
        ...state,
        runStatus: "cancelled",
        errorMessage: null,
      };
    case "RESET_TO_INPUT":
      return {
        ...initialRunState(),
        prompt: state.prompt,
        vectorStore: state.vectorStore,
        defaultVectorStore: state.defaultVectorStore,
        departmentId: state.departmentId,
        departmentLabel: state.departmentLabel,
        departments: state.departments,
      };
    case "GO_RESULTS":
      return { ...state, screen: "results", showLiveLogOverlay: false };
    case "SHOW_LIVE_LOG":
      return { ...state, showLiveLogOverlay: action.show };
    case "RUN_ERROR":
      return {
        ...state,
        runStatus: "error",
        errorMessage: action.message,
      };
    case "LOAD_SAVED_RUN": {
      const { manifest, papers, answer } = action.run;
      const paperList = papers?.papers ?? [];
      const base = action.preserveSession
        ? {
            log: state.log,
            logEventCount: state.logEventCount,
            showLiveLogOverlay: state.showLiveLogOverlay,
            specialistText: state.specialistText,
          }
        : {};
      return {
        ...initialRunState(),
        ...base,
        screen: "results",
        prompt: manifest.query ?? state.prompt,
        runId: manifest.run_id ?? papers.run_id ?? answer.run_id ?? null,
        runStatus: "complete",
        durationMs: manifest.duration_ms ?? state.durationMs,
        paperCount: manifest.paper_count ?? paperList.length,
        papers: paperList,
        framework: manifest.framework ?? null,
        rewrittenQuery: manifest.rewritten_query ?? null,
        picot: normalizePicot(manifest.picot),
        spice: normalizeSpice(manifest.spice),
        supervisorAnswer: answer.answer ?? "",
        consensus: answer.consensus ?? null,
        followups: answer.followup_questions ?? [],
        specialistsUsed: manifest.specialists_used ?? [],
        manifest,
        modelId: manifest.model_id ?? null,
        departmentId: manifest.department ?? state.departmentId,
        departmentLabel: manifest.department_label ?? state.departmentLabel,
        departments: state.departments,
        vectorStore:
          (manifest.vector_backend as RunState["vectorStore"]) ?? state.vectorStore,
        defaultVectorStore: state.defaultVectorStore,
        phaseStatus: ["done", "done", "done", "done", "done"],
        phaseElapsedMs: [null, null, null, null, null],
      };
    }
    case "LOAD_SAVED_RUN_ERROR":
      return { ...state, runStatus: "error", errorMessage: action.message };
    case "SSE_EVENT":
      return applySseEvent(state, action.event);
    default:
      return state;
  }
}

function applySseEvent(state: RunState, event: SseEvent): RunState {
  const now = Date.now();
  const phaseCtx = clonePhaseCtx(state, now);
  phaseFromEvent(event, phaseCtx);

  let activeTool = state.activeTool;
  if (event.type === "tool_start" || event.type === "tool_running") {
    if (event.name) activeTool = event.name;
  } else if (event.type === "tool_end") {
    if (!event.name || event.name === activeTool) activeTool = null;
  }

  let specialistText = state.specialistText;
  const toolSpecKey = specialistKeyFromTool(event.name);
  if (toolSpecKey) {
    specialistText = ensureSpecialistKeys(state, [toolSpecKey]);
  }

  let next: RunState = {
    ...state,
    phaseStatus: phaseCtx.phaseStatus,
    phaseStartedAt: phaseCtx.phaseStartedAt,
    phaseElapsedMs: phaseCtx.phaseElapsedMs,
    phase3WasActive: phaseCtx.phase3WasActive,
    log: upsertLog(state, event),
    logEventCount: state.logEventCount + 1,
    activeTool,
    lastEventMs: now,
    specialistText,
  };

  if (state.runStatus === "connecting" && event.type !== "error") {
    next.runStatus = "running";
  }

  switch (event.type) {
    case "run_start": {
      const d = event.data as { run_id?: string };
      next.runId = d?.run_id ?? next.runId;
      next.runStartMs = now;
      break;
    }
    case "query_rewritten": {
      const d = event.data as {
        rewritten_query?: string;
        framework?: string;
        picot?: PicotSpice;
        spice?: PicotSpice;
      };
      next.rewrittenQuery = d.rewritten_query ?? null;
      next.framework = d.framework ?? null;
      next.picot = normalizePicot(d.picot);
      next.spice = normalizeSpice(d.spice);
      break;
    }
    case "query_classified": {
      const d = event.data as {
        detected_domains?: string[];
        complexity?: string;
        patient_context?: string;
      };
      next.detectedDomains = d.detected_domains ?? [];
      next.complexity = d.complexity ?? null;
      next.patientContext = d.patient_context ?? null;
      if (next.detectedDomains.length) {
        next.specialistText = ensureSpecialistKeys(next, next.detectedDomains);
      }
      break;
    }
    case "specialist_papers": {
      if (!event.specialist) break;
      const spec = ensureSpecialist(next, event.specialist);
      next.specialistText = {
        ...next.specialistText,
        [event.specialist]: { ...spec, paperCount: event.count ?? spec.paperCount },
      };
      break;
    }
    case "specialist_text": {
      if (!event.specialist) break;
      const spec = ensureSpecialist(next, event.specialist);
      next.specialistText = {
        ...next.specialistText,
        [event.specialist]: {
          ...spec,
          text: spec.text + String(event.data ?? ""),
        },
      };
      break;
    }
    case "specialist_tool": {
      if (!event.specialist) break;
      const spec = ensureSpecialist(next, event.specialist);
      next.specialistText = {
        ...next.specialistText,
        [event.specialist]: { ...spec, lastTool: event.tool ?? null },
      };
      break;
    }
    case "text":
      next.supervisorAnswer += String(event.data ?? "");
      break;
    case "thinking":
      next.reasoning += String(event.data ?? "");
      break;
    case "papers": {
      const d = event.data as { papers?: Paper[]; count?: number };
      const count = d.count ?? d.papers?.length ?? next.paperCount;
      next.paperCount = Math.max(next.paperCount, count);
      if (d.papers?.length) next.papers = d.papers;
      break;
    }
    case "tool_result": {
      if (event.papers?.length) {
        next.papers = event.papers;
        next.paperCount = Math.max(next.paperCount, event.papers.length);
      }
      break;
    }
    case "consensus":
      next.consensus = event.data as Consensus;
      break;
    case "followups": {
      const d = event.data as { followups?: string[] };
      next.followups = d.followups ?? [];
      break;
    }
    case "cci":
      next.cci = event.data as Record<string, number>;
      break;
    case "run_end": {
      const d = event.data as {
        run_id?: string;
        duration_ms?: number;
        paper_count?: number;
        specialists_used?: string[];
        rewritten_query?: string;
      };
      doneAllPhases(phaseCtx);
      next.phaseStatus = phaseCtx.phaseStatus;
      next.phaseElapsedMs = phaseCtx.phaseElapsedMs;
      next.runId = d.run_id ?? next.runId;
      next.durationMs = d.duration_ms ?? (next.runStartMs ? now - next.runStartMs : null);
      next.paperCount = Math.max(next.paperCount, d.paper_count ?? 0);
      next.specialistsUsed = d.specialists_used ?? next.specialistsUsed;
      if (d.rewritten_query) next.rewrittenQuery = d.rewritten_query;
      next.runStatus = "complete";
      next.manifest = {
        run_id: next.runId ?? undefined,
        query: state.prompt,
        rewritten_query: next.rewrittenQuery ?? undefined,
        framework: next.framework ?? undefined,
        picot: next.picot ?? undefined,
        spice: next.spice ?? undefined,
        specialists_used: next.specialistsUsed,
        vector_backend: state.vectorStore,
        model_id: next.modelId ?? undefined,
        duration_ms: next.durationMs ?? undefined,
        paper_count: next.paperCount,
        cci: next.cci ?? undefined,
      };
      break;
    }
    case "done":
      next.screen = "results";
      break;
    case "error":
      next.runStatus = "error";
      next.errorMessage = String(event.data ?? "An error occurred.");
      break;
    case "warning":
      next.warningMessage = String(event.data ?? "");
      break;
    default:
      break;
  }

  return next;
}
